/**
 * Collection / featured-collection cards: show merge_* sibling product variants.
 */
const fetchInflight = window.__mergedCardFetches || (window.__mergedCardFetches = {});

function cacheKey(tag) {
  return `merged-variants:${String(tag || '').toLowerCase()}`;
}

function readCache(tag) {
  try {
    return JSON.parse(sessionStorage.getItem(cacheKey(tag)) || 'null');
  } catch (_) {
    return null;
  }
}

function writeCache(tag, products) {
  try {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    sessionStorage.setItem(
      cacheKey(tag),
      JSON.stringify({
        updatedAt: Date.now(),
        products: products.map((product) => ({
          id: product.id,
          handle: product.handle,
          title: product.title,
          url: product.url || `${root}products/${product.handle}`,
          tags: product.tags,
          featured_image: product.featured_image,
          variants: (product.variants || []).map((variant) => ({
            id: variant.id,
            title: variant.title,
            option1: variant.option1,
            available: variant.available,
            featured_image: variant.featured_image,
          })),
        })),
      })
    );
  } catch (_) {}
}

function hasTag(product, mergeTag) {
  const wanted = String(mergeTag).toLowerCase();
  const tags = product.tags;
  if (Array.isArray(tags)) return tags.some((tag) => String(tag).trim().toLowerCase() === wanted);
  if (typeof tags === 'string') {
    return tags.split(',').map((tag) => tag.trim().toLowerCase()).includes(wanted);
  }
  return false;
}

function uniqueTagged(products, mergeTag) {
  const filtered = [];
  const seen = new Set();
  products.forEach((product) => {
    if (!hasTag(product, mergeTag)) return;
    const id = Number(product.id);
    if (seen.has(id)) return;
    seen.add(id);
    filtered.push(product);
  });
  filtered.sort((a, b) => {
    const handleCompare = String(a.handle || '').localeCompare(String(b.handle || ''));
    if (handleCompare !== 0) return handleCompare;
    return Number(a.id) - Number(b.id);
  });
  return filtered;
}

function handleize(value) {
  return String(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-+/g, '-');
}

function imageUrl(src, width) {
  if (!src) return '';
  if (src.indexOf('{width}') !== -1) return src.replace('{width}', String(width));
  let next = src;
  if (next.indexOf('//') === 0) next = `https:${next}`;
  try {
    const url = new URL(next, window.location.origin);
    url.searchParams.set('width', String(width));
    return url.toString();
  } catch (_) {
    return src;
  }
}

function variantImage(product, variant) {
  const featured = variant && variant.featured_image;
  if (featured) {
    if (typeof featured === 'string') return featured;
    if (featured.src) return featured.src;
  }
  if (product.featured_image) {
    return typeof product.featured_image === 'string' ? product.featured_image : product.featured_image.src;
  }
  return '';
}

function variantLabel(product, variant) {
  if (
    product.variants &&
    product.variants.length === 1 &&
    (variant.title === 'Default Title' || variant.option1 === 'Default Title')
  ) {
    return product.title;
  }
  if (variant.title && variant.title !== 'Default Title') return variant.title;
  return product.title;
}

function variantHref(product, variant) {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  try {
    const url = new URL(product.url || `${root}products/${product.handle}`, window.location.origin);
    url.searchParams.set('variant', String(variant.id));
    return `${url.pathname}${url.search}`;
  } catch (_) {
    return `${product.url || `${root}products/${product.handle}`}?variant=${variant.id}`;
  }
}

async function fetchProductJs(handle, root) {
  try {
    return await fetch(`${root}products/${handle}.js`).then((response) => response.json());
  } catch (_) {
    return null;
  }
}

async function fetchMergedProducts(mergeTag) {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const handle = handleize(mergeTag);
  const results = [];

  try {
    const response = await fetch(`${root}collections/${handle}/products.json?limit=50`);
    if (response.ok) {
      const data = await response.json();
      const list = await Promise.all(
        (data.products || []).map((product) => fetchProductJs(product.handle, root))
      );
      list.filter(Boolean).forEach((product) => results.push(product));
    }
  } catch (_) {}

  if (uniqueTagged(results, mergeTag).length >= 2) return uniqueTagged(results, mergeTag);

  try {
    const viewUrl = `${root}search?q=${encodeURIComponent(mergeTag)}&type=product&view=merge-products`;
    const viewResponse = await fetch(viewUrl, { headers: { Accept: 'application/json' } });
    if (viewResponse.ok) {
      const text = await viewResponse.text();
      const jsonStart = text.indexOf('{');
      if (jsonStart !== -1) {
        const data = JSON.parse(text.slice(jsonStart));
        const list = await Promise.all(
          (data.products || []).map((item) => fetchProductJs(item.handle, root))
        );
        list.filter(Boolean).forEach((product) => results.push(product));
      }
    }
  } catch (_) {}

  return uniqueTagged(results, mergeTag);
}

function loadMergedProducts(tag) {
  const cached = readCache(tag);
  if (cached?.products?.length >= 2) return Promise.resolve(uniqueTagged(cached.products, tag));

  const key = cacheKey(tag);
  if (!fetchInflight[key]) {
    fetchInflight[key] = fetchMergedProducts(tag).then((products) => {
      if (products.length >= 2) writeCache(tag, products);
      return products;
    });
  }
  return fetchInflight[key];
}

function uniqueProductCount(root) {
  const ids = new Set();
  root.querySelectorAll('a[data-product-id]').forEach((anchor) => ids.add(anchor.dataset.productId));
  return ids.size;
}

function mediaSrcFromAnchor(anchor) {
  if (!anchor) return '';
  if (anchor.dataset.mediaSrc) return anchor.dataset.mediaSrc;
  const thumb = anchor.querySelector('img');
  return (thumb && (thumb.currentSrc || thumb.src)) || '';
}

function updateCardImage(card, src) {
  const image = card && card.querySelector('.product-card__image--primary');
  if (!image || !src) return;
  const nextSrc = imageUrl(src, 800);
  if (!nextSrc) return;
  image.setAttribute('src', nextSrc);
  image.removeAttribute('srcset');
  image.removeAttribute('sizes');
}

function updateCardLinks(card, href) {
  if (!card || !href) return;
  card.querySelectorAll('a[href*="/products/"]').forEach((link) => {
    if (link.closest('merged-card-swatches')) return;
    link.setAttribute('href', href);
  });
  if (card.hasAttribute('handle')) {
    try {
      const url = new URL(href, window.location.origin);
      const parts = url.pathname.split('/').filter(Boolean);
      const handle = parts[parts.length - 1];
      if (handle) card.setAttribute('handle', handle);
    } catch (_) {}
  }
}

class MergedCardSwatches extends HTMLElement {
  connectedCallback() {
    if (this.dataset.booted === 'true') return;
    this.dataset.booted = 'true';

    this._values = this.querySelector('[data-merged-card-values]');
    this._card = this.closest('product-card');
    this._mergeTag = this.dataset.mergeTag;
    this._currentProductId = Number(this.dataset.currentProductId);
    this._currentVariantId = Number(this.dataset.currentVariantId);

    this._bind();
    this._hydrate();
  }

  _bind() {
    if (!this._values || this._values.dataset.bound === 'true') return;
    this._values.dataset.bound = 'true';

    this._values.addEventListener(
      'click',
      (event) => {
        const anchor = event.target.closest('a.thumbnail-swatch');
        if (!anchor || !this._values.contains(anchor)) return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        this._currentProductId = Number(anchor.dataset.productId);
        this._currentVariantId = Number(anchor.dataset.variantId);
        this.dataset.currentProductId = String(this._currentProductId);
        this.dataset.currentVariantId = String(this._currentVariantId);

        this._setSelected(anchor);
        updateCardImage(this._card, mediaSrcFromAnchor(anchor));
        updateCardLinks(this._card, anchor.getAttribute('href'));
      },
      true
    );
  }

  async _hydrate() {
    if (!this._mergeTag) return;
    if (uniqueProductCount(this) >= 2) return;

    const products = await loadMergedProducts(this._mergeTag);
    if (!this.isConnected || !products || products.length < 2) return;
    this._render(products);
  }

  _render(products) {
    if (!this._values) return;
    const currentVariantId = String(this.dataset.currentVariantId || this._currentVariantId);
    const fragment = document.createDocumentFragment();

    products.forEach((product) => {
      (product.variants || []).forEach((variant) => {
        const isSelected = String(variant.id) === currentVariantId;
        const label = variantLabel(product, variant);
        const href = variantHref(product, variant);
        const img = variantImage(product, variant);
        const available = variant.available !== false;

        const anchor = document.createElement('a');
        anchor.href = href;
        anchor.className = `thumbnail-swatch thumbnail-swatch--sm border${isSelected ? ' is-selected' : ''}${
          available ? '' : ' is-disabled'
        }`;
        anchor.title = label;
        anchor.dataset.productId = String(product.id);
        anchor.dataset.variantId = String(variant.id);
        anchor.dataset.variantLabel = label;
        if (img) anchor.dataset.mediaSrc = imageUrl(img, 800);
        if (isSelected) anchor.setAttribute('aria-current', 'true');

        const sr = document.createElement('span');
        sr.className = 'sr-only';
        sr.textContent = label;
        anchor.appendChild(sr);

        if (img) {
          const image = document.createElement('img');
          image.className = 'object-cover';
          image.alt = label;
          image.width = 48;
          image.height = 48;
          image.loading = 'lazy';
          image.src = imageUrl(img, 96);
          anchor.appendChild(image);
        }

        fragment.appendChild(anchor);
      });
    });

    this._values.replaceChildren(fragment);
    this._values.dataset.bound = '';
    this._bind();
  }

  _setSelected(anchor) {
    this._values.querySelectorAll('a.thumbnail-swatch').forEach((node) => {
      const selected = node === anchor;
      node.classList.toggle('is-selected', selected);
      if (selected) node.setAttribute('aria-current', 'true');
      else node.removeAttribute('aria-current');
    });
  }
}

if (!customElements.get('merged-card-swatches')) {
  customElements.define('merged-card-swatches', MergedCardSwatches);
}
